import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/lib/supabaseClient";
import {
  getMatchMessages,
  sendMatchMessage as apiSendMatchMessage,
  editMatchMessage,
  deleteMatchMessage,
  reactToMatchMessage,
  sendMatchTyping,
  confirmMatchDelivery,
} from "@/lib/api";
import { compressImageToDataUri } from "@/lib/imageUtils";

const HEARTBEAT_MS = 45000;
const TYPING_RESET_MS = 4000;
const TYPING_COOLDOWN_MS = 2000;

export function useMatchChat(match, currentUserId) {
  const matchId = match?.match_id;

  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [partnerTyping, setPartnerTyping] = useState(false);
  const [partnerLastActive, setPartnerLastActive] = useState(null);
  const [online, setOnline] = useState(true);

  const lastSeqRef = useRef(0);
  const stateTsRef = useRef(null);
  const fetchInFlightRef = useRef(false);
  const deadRef = useRef(false);

  const heartbeatRef = useRef(null);
  const partnerTypingResetRef = useRef(null);
  const typingCooldownRef = useRef(null);

  const applyStateChange = useCallback((msg, change) => {
    const isDeleted = change.deleted === true;
    const next = { ...msg, deleted: isDeleted, edited: change.edited === true };
    if (isDeleted) {
      next.content = "";
      next.media_url = null;
    } else {
      if (change.content != null) next.content = String(change.content);
      if ("media_url" in change) next.media_url = change.media_url || null;
    }
    if (msg.sender_id === currentUserId) next.read = change.read === true;
    next.reactions = change.reactions || {};
    return next;
  }, [currentUserId]);

  const fetchMessages = useCallback(async ({ initial = false } = {}) => {
    if (!matchId || fetchInFlightRef.current || deadRef.current) return;
    fetchInFlightRef.current = true;
    try {
      const params = initial
        ? { limit: 50 }
        : {
            limit: 100,
            after_seq: lastSeqRef.current || undefined,
            state_since: stateTsRef.current || undefined,
          };

      const data = await getMatchMessages(matchId, params);
      if (deadRef.current) return;

      const list = Array.isArray(data?.messages) ? data.messages : [];
      const changes = Array.isArray(data?.state_changes) ? data.state_changes : [];

      setMessages(prev => {
        let next = prev.slice();

        if (changes.length) {
          const changeMap = new Map(changes.map(c => [c.message_id, c]));
          next = next.map(m => changeMap.has(m.message_id)
            ? applyStateChange(m, changeMap.get(m.message_id))
            : m);
        }

        const byId = new Set(next.map(m => m.message_id));
        const byClient = new Set(next.map(m => m.client_message_id).filter(Boolean));
        const additions = [];

        for (const raw of list) {
          if (!raw || byId.has(raw.message_id)) continue;

          if (raw.client_message_id && byClient.has(raw.client_message_id)) {
            const idx = next.findIndex(
              m => m.client_message_id === raw.client_message_id
            );
            if (idx !== -1) {
              next[idx] = { ...raw, _optimistic: false, _status: "sent" };
              byId.add(raw.message_id);
              continue;
            }
          }

          additions.push({ ...raw, _optimistic: false });
          byId.add(raw.message_id);
          if (raw.client_message_id) byClient.add(raw.client_message_id);
        }

        if (additions.length) {
          additions.sort((a, b) => (a.seq || 0) - (b.seq || 0));
          next = [...next, ...additions];
        }
        return next;
      });

      const maxSeq = list.reduce(
        (m, x) => (x.seq != null && x.seq > m ? x.seq : m),
        lastSeqRef.current
      );
      if (maxSeq > lastSeqRef.current) lastSeqRef.current = maxSeq;
      if (data?.server_ts) stateTsRef.current = data.server_ts;

      setPartnerTyping(!!data?.partner_typing);
      setPartnerLastActive(data?.partner_last_active || null);
      setOnline(true);

      const theirMax = list.reduce(
        (acc, m) => (m.sender_id !== currentUserId && m.seq > acc ? m.seq : acc),
        0
      );
      if (theirMax > 0) confirmMatchDelivery(matchId, theirMax);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 403 || status === 404) {
        deadRef.current = true;
        setOnline(false);
        throw new Error("dead");
      }
      setOnline(false);
    } finally {
      fetchInFlightRef.current = false;
    }
  }, [matchId, currentUserId, applyStateChange]);

  useEffect(() => {
    if (!matchId) return;
    setLoading(true);
    deadRef.current = false;
    lastSeqRef.current = 0;
    stateTsRef.current = null;
    fetchMessages({ initial: true })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [matchId, fetchMessages]);

  useEffect(() => {
    if (!matchId) return;
    heartbeatRef.current = setInterval(() => {
      fetchMessages().catch(() => {});
    }, HEARTBEAT_MS);
    return () => clearInterval(heartbeatRef.current);
  }, [matchId, fetchMessages]);

  useEffect(() => {
    if (!matchId || !currentUserId) return;
    if (!supabase) return;   // guard against missing env vars

    // Backend broadcasts on `user:{user_id}` — colon, not dash.
    const channel = supabase.channel(`user:${currentUserId}`);

    channel.on("broadcast", { event: "poke" }, (payload) => {
      const pid = payload?.payload?.match_id;
      if (!pid || pid === matchId) fetchMessages().catch(() => {});
    });

    channel.on("broadcast", { event: "typing" }, (payload) => {
      const pid = payload?.payload?.match_id;
      if (!pid || pid !== matchId) return;
      setPartnerTyping(true);
      clearTimeout(partnerTypingResetRef.current);
      partnerTypingResetRef.current = setTimeout(
        () => setPartnerTyping(false),
        TYPING_RESET_MS
      );
    });

    channel.subscribe();
    return () => { try { channel.unsubscribe(); } catch (_) {} };
  }, [matchId, currentUserId, fetchMessages]);

  // ── Send text
  const sendMessage = useCallback(async (content, opts = {}) => {
    if (!matchId) return;
    const text = content.trim();
    if (!text) return;

    const clientId = `cli_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const optimistic = {
      message_id: `pending_${clientId}`,
      client_message_id: clientId,
      sender_id: currentUserId,
      content: text,
      message_type: "text",
      media_url: null,
      reply_to_id: opts.replyToId || null,
      reply_to: opts.replyTo || null,
      reactions: {},
      read: false,
      deleted: false,
      edited: false,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      seq: lastSeqRef.current,
      _optimistic: true,
      _status: "sending",
    };

    setMessages(prev => [...prev, optimistic]);

    try {
      const res = await apiSendMatchMessage(matchId, text, {
        clientMessageId: clientId,
        replyToId: opts.replyToId,
      });
      const server = res?.message;
      if (server) {
        setMessages(prev => prev.map(m => m.client_message_id === clientId
          ? { ...server, _optimistic: false, _status: "sent" }
          : m));
        if (server.seq && server.seq > lastSeqRef.current) {
          lastSeqRef.current = server.seq;
        }
      }
    } catch (err) {
      setMessages(prev => prev.map(m => m.client_message_id === clientId
        ? { ...m, _status: "failed" }
        : m));
      throw err;
    }
  }, [matchId, currentUserId]);

  // ── Send image
  const sendImage = useCallback(async (file, opts = {}) => {
    if (!matchId || !file) return;
    const clientId = `cli_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

    let dataUri;
    try {
      const { dataUri: uri } = await compressImageToDataUri(file);
      dataUri = uri;
    } catch (e) {
      throw new Error("Could not process image");
    }

    const optimistic = {
      message_id: `pending_${clientId}`,
      client_message_id: clientId,
      sender_id: currentUserId,
      content: "",
      message_type: "image",
      media_url: dataUri,
      reply_to_id: opts.replyToId || null,
      reply_to: opts.replyTo || null,
      reactions: {},
      read: false,
      deleted: false,
      edited: false,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      seq: lastSeqRef.current,
      _optimistic: true,
      _status: "sending",
    };

    setMessages(prev => [...prev, optimistic]);

    try {
      const res = await apiSendMatchMessage(matchId, "", {
        clientMessageId: clientId,
        replyToId: opts.replyToId,
        messageType: "image",
        mediaUrl: dataUri,
      });
      const server = res?.message;
      if (server) {
        setMessages(prev => prev.map(m => m.client_message_id === clientId
          ? { ...server, _optimistic: false, _status: "sent" }
          : m));
        if (server.seq && server.seq > lastSeqRef.current) {
          lastSeqRef.current = server.seq;
        }
      }
    } catch (err) {
      setMessages(prev => prev.map(m => m.client_message_id === clientId
        ? { ...m, _status: "failed" }
        : m));
      throw err;
    }
  }, [matchId, currentUserId]);

  const editMessage = useCallback(async (messageId, newContent) => {
    if (!matchId) return;
    const text = newContent.trim();
    if (!text) return;
    await editMatchMessage(matchId, messageId, text);
    setMessages(prev => prev.map(m => m.message_id === messageId
      ? { ...m, content: text, edited: true }
      : m));
  }, [matchId]);

  const deleteMessage = useCallback(async (messageId) => {
    if (!matchId) return;
    await deleteMatchMessage(matchId, messageId);
    setMessages(prev => prev.map(m => m.message_id === messageId
      ? { ...m, deleted: true, content: "", media_url: null }
      : m));
  }, [matchId]);

  const reactToMessage = useCallback(async (messageId, emoji) => {
    if (!matchId) return;
    const res = await reactToMatchMessage(matchId, messageId, emoji);
    setMessages(prev => prev.map(m => m.message_id === messageId
      ? { ...m, reactions: res?.reactions || {} }
      : m));
  }, [matchId]);

  const notifyTyping = useCallback(() => {
    if (typingCooldownRef.current) return;
    sendMatchTyping(matchId);
    typingCooldownRef.current = setTimeout(() => {
      typingCooldownRef.current = null;
    }, TYPING_COOLDOWN_MS);
  }, [matchId]);

  useEffect(() => () => {
    clearTimeout(partnerTypingResetRef.current);
    clearTimeout(typingCooldownRef.current);
    clearInterval(heartbeatRef.current);
  }, []);

  return {
    messages,
    loading,
    partnerTyping,
    partnerLastActive,
    online,
    dead: deadRef.current,
    sendMessage,
    sendImage,
    editMessage,
    deleteMessage,
    reactToMessage,
    notifyTyping,
    refresh: fetchMessages,
  };
}